import { useState } from 'react';
import { Plus, Edit, Trash2, Building2, Home, Briefcase, Store, House, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { useCategories } from '../../contexts/CategoriesContext';
import { categories as mockCategories } from '../../data/mockData';
import { supabase } from '../../lib/supabase';

const iconOptions = [
  { value: 'Building2', label: 'Building2' },
  { value: 'Home', label: 'Home' },
  { value: 'Briefcase', label: 'Briefcase' },
  { value: 'Store', label: 'Store' },
  { value: 'House', label: 'House' },
];

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Building2,
  Home,
  Briefcase,
  Store,
  House,
};

interface Category {
  id: string;
  name: string;
  nameEn: string;
  icon: string;
  count?: number;
}

export default function AdminCategories() {
  const { categories, loading, addCategory, updateCategory, deleteCategory, refreshCategories, refreshCategoryCounts } = useCategories();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addForm, setAddForm] = useState({
    name: '',
    nameEn: '',
    icon: 'Building2',
  });

  // Form states for edit dialog
  const [editForm, setEditForm] = useState({
    name: '',
    nameEn: '',
    icon: 'Building2',
  });

  const handleDelete = async (id: string) => {
    if (!confirm('هل أنت متأكد من حذف هذا التصنيف؟')) return;
    
    try {
      setSaving(true);
      await deleteCategory(id);
    } catch (err) {
      console.error('Error deleting category:', err);
      alert('حدث خطأ أثناء حذف التصنيف');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setEditForm({
      name: category.name,
      nameEn: category.nameEn,
      icon: category.icon,
    });
    setIsEditDialogOpen(true);
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!addForm.name.trim() || !addForm.nameEn.trim()) {
      alert('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    try {
      setSaving(true);
      await addCategory({
        name: addForm.name.trim(),
        nameEn: addForm.nameEn.trim(),
        icon: addForm.icon,
      });
      setAddForm({ name: '', nameEn: '', icon: 'Building2' });
      setIsAddDialogOpen(false);
    } catch (err) {
      console.error('Error adding category:', err);
      alert('حدث خطأ أثناء إضافة التصنيف');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingCategory) return;
    
    if (!editForm.name.trim() || !editForm.nameEn.trim()) {
      alert('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    try {
      setSaving(true);
      await updateCategory(editingCategory.id, {
        name: editForm.name.trim(),
        nameEn: editForm.nameEn.trim(),
        icon: editForm.icon,
      });
      setEditingCategory(null);
      setIsEditDialogOpen(false);
    } catch (err) {
      console.error('Error updating category:', err);
      alert('حدث خطأ أثناء تحديث التصنيف');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncCategories = async () => {
    if (!confirm('سيتم مزامنة التصنيفات الافتراضية إلى قاعدة البيانات. هل تريد المتابعة؟')) return;
    
    try {
      setSyncing(true);
      
      // Transform mock categories to database format
      const categoriesToInsert = mockCategories.map(cat => ({
        id: cat.id,
        name: cat.name,
        name_en: cat.nameEn,
        icon: cat.icon,
      }));
      
      // Insert with upsert (insert or update if exists)
      const { error } = await supabase
        .from('categories')
        .upsert(categoriesToInsert, { onConflict: 'id' });
      
      if (error) throw error;
      
      alert('تم مزامنة التصنيفات بنجاح!');
      refreshCategories(); // Refresh the list from context
    } catch (err) {
      console.error('Error syncing categories:', err);
      alert('حدث خطأ أثناء مزامنة التصنيفات');
    } finally {
      setSyncing(false);
    }
  };
  const renderIcon = (iconName: string) => {
    const IconComponent = iconMap[iconName] || Building2;
    return <IconComponent className="w-6 h-6" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={refreshCategories} className="bg-[#D4AF37] hover:bg-[#B8960C] text-white">
          إعادة المحاولة
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* Debug Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-bold text-blue-800 mb-2">Debug Info:</h3>
        <p className="text-sm text-blue-600">Total Categories: {categories.length}</p>
        <p className="text-sm text-blue-600">Loading: {loading ? 'Yes' : 'No'}</p>
        <div className="text-sm text-blue-600 mt-2">
          Category Counts:
          {categories.map(cat => (
            <span key={cat.id} className="ml-2">
              {cat.name}: {cat.count || 0}
            </span>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">إدارة التصنيفات</h1>
          <p className="text-gray-600">إضافة وتعديل وحذف تصنيفات العقارات</p>
        </div>

        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={refreshCategoryCounts}
            disabled={loading}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh Counts'}
          </Button>
          <Button 
            variant="outline" 
            onClick={handleSyncCategories}
            disabled={syncing}
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sync Categories'}
          </Button>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4AF37] hover:bg-[#B8960C] text-white">
              <Plus className="w-5 h-5 ml-2" />
              إضافة تصنيف جديد
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>إضافة تصنيف جديد</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddSubmit} className="space-y-4">
              {/* Quick Add Predefined Categories */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <label className="block text-sm font-medium mb-2 text-gray-700">إضافة سريعة:</label>
                <div className="flex flex-wrap gap-2">
                  {mockCategories
                    .filter(mc => !categories.some(c => c.nameEn === mc.nameEn))
                    .map(cat => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={async () => {
                          try {
                            await addCategory({
                              name: cat.name,
                              nameEn: cat.nameEn,
                              icon: cat.icon,
                            });
                          } catch (err) {
                            console.error('Error adding category:', err);
                          }
                        }}
                        disabled={saving}
                        className="px-3 py-1.5 bg-white border border-[#D4AF37] text-[#8B7109] rounded-full text-sm hover:bg-[#D4AF37] hover:text-white transition-colors disabled:opacity-50"
                      >
                        + {cat.name}
                      </button>
                    ))}
                  {mockCategories.every(mc => categories.some(c => c.nameEn === mc.nameEn)) && (
                    <span className="text-sm text-gray-500">جميع التصنيفات المحددة مضافة بالفعل</span>
                  )}
                </div>
              </div>

              <div className="border-t pt-4">
                <label className="block text-sm font-medium mb-2">اسم التصنيف (عربي) *</label>
                <Input 
                  placeholder="مثال: شقق" 
                  value={addForm.name}
                  onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">اسم التصنيف (إنجليزي) *</label>
                <Input 
                  placeholder="Example: Apartments" 
                  value={addForm.nameEn}
                  onChange={(e) => setAddForm({ ...addForm, nameEn: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">الأيقونة</label>
                <select 
                  className="w-full px-3 py-2 border rounded-lg"
                  value={addForm.icon}
                  onChange={(e) => setAddForm({ ...addForm, icon: e.target.value })}
                >
                  {iconOptions.map(icon => (
                    <option key={icon.value} value={icon.value}>{icon.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1 bg-[#D4AF37] hover:bg-[#B8960C] text-white"
                  disabled={saving}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'حفظ التصنيف'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setAddForm({ name: '', nameEn: '', icon: 'Building2' });
                    setIsAddDialogOpen(false);
                  }}
                  disabled={saving}
                >
                  إلغاء
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <div key={category.id} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                  <span className="text-white">
                    {renderIcon(category.icon)}
                  </span>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800">{category.name}</h3>
                  <p className="text-sm text-gray-600">{category.nameEn}</p>
                </div>
              </div>
            </div>

            <div className="mb-4 pb-4 border-b">
              <div className="text-sm text-gray-600">عدد العقارات</div>
              <div className="text-2xl font-bold text-[#8B7109]">{category.count || 0}</div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => handleEdit(category)}
              >
                <Edit className="w-4 h-4 ml-2" />
                تعديل
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => handleDelete(category.id)}
                disabled={saving}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>تعديل التصنيف</DialogTitle>
          </DialogHeader>
          {editingCategory && (
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">اسم التصنيف (عربي) *</label>
                <Input 
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">اسم التصنيف (إنجليزي) *</label>
                <Input 
                  value={editForm.nameEn}
                  onChange={(e) => setEditForm({ ...editForm, nameEn: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">الأيقونة</label>
                <select 
                  className="w-full px-3 py-2 border rounded-lg"
                  value={editForm.icon}
                  onChange={(e) => setEditForm({ ...editForm, icon: e.target.value })}
                >
                  {iconOptions.map(icon => (
                    <option key={icon.value} value={icon.value}>{icon.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1 bg-[#D4AF37] hover:bg-[#B8960C] text-white"
                  disabled={saving}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'حفظ التعديلات'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setEditingCategory(null);
                    setIsEditDialogOpen(false);
                  }}
                  disabled={saving}
                >
                  إلغاء
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}